import React, { useEffect, useState } from "react";

import { useTunnelData } from "../TunnelContext";
import Api from "../baseApi";
import OAButton from "../components/OAButton";
import OAMaskInput from "../components/input/OAMaskInput";
import Breadcrumbs from "../components/Breadcrumbs";
import { useTranslation } from "react-i18next";
import { Redirect } from "react-router-dom/cjs/react-router-dom.min";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import LoadingScreen from "../components/LoadingScreen";
import Footer from "../components/Footer";
import pushEvent from "../pushEvent";

export default function Contract() {
  const { tunnel, setTunnel } = useTunnelData();
  const [contract, setContract] = useState(false);
  const [error, setError] = useState(false);
  const [step, setStep] = useState("CONTRACT");
  const { t } = useTranslation();
  const [smsSending, smsSendingSet] = useState(false);
  const [code, codeSet] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setError(false);
    const [query] = Api.GET({
      path: "/api/abo/resamania2/contract",
      query: {
        tunnelUid: tunnel.uid,
        msdsId: tunnel.club.msdsId,
        sale: tunnel.sales["@id"],
      },
    });

    query
      .then((ret) => {
        setContract(ret.contract);
        askToSign().catch((e) => {
          console.error("Cannot auto ask for sms", e);
        });
      })
      .catch((e) => {
        console.error(e);
        setError("Nous ne pouvons afficher le contrat");
      });
  }, [tunnel.sales, tunnel.club]);

  if (!tunnel.sales) return <Redirect push to={"/"} />;

  const onSubmit = async (e) => {
    e.preventDefault();

    pushEvent({
      event: "funnel_step_4_sign",
      step_name: "Abonnement",
      funnel_club: tunnel.club.name,
      funnel_club_status: "Ouvert",
      funnel_abo: tunnel.offer.offer.name,
    });
    const codeClean = code.replace(/[^0-9]/g, "");
    setTunnel({ ...tunnel, signCode: codeClean });
    setStep("LOADING");
    setError(false);

    try {
      const [signContractWithCode] = Api.POST({
        path: "/api/abo/resamania2/end_sign_mandates",
        body: {
          tunnelUid: tunnel.uid,
          msdsId: tunnel.club.msdsId,
          tunnel: tunnel,
          end_sign_mandate: {
            code: codeClean,
            transition: "validate",
            signatureRequestId: tunnel.sign_request["@id"],
          },
        },
      });

      await signContractWithCode;

      try {
        if (tunnel.sales.totalTI === 0) {
          await doPaymentManuelDues();
          setStep("CONFIRM");
          return;
        }
        setStep("CARD");
      } catch (e) {
        console.error(e);
        setError("La finalisation du contrat a échouée");
        setStep("FORM");
      }
    } catch (e) {
      console.error(e);
      setError("Le code SMS n'est pas valide");
      setStep("FORM");
    }
  };

  const doPaymentManuelDues = async () => {
    const [query] = Api.POST({
      path: "/api/abo/resamania2/payment",
      body: {
        tunnelUid: tunnel.uid,
        msdsId: tunnel.club.msdsId,
        payment: {
          saleId: tunnel.sales["@id"],
          contactId: tunnel.contactResa["@id"],
          contactFamilyName: tunnel.contactResa.familyName,
          contactGivenName: tunnel.contactResa.givenName,
          contactNumber: tunnel.contactResa.number,
          amount: tunnel.sales.totalTI,
          clubId: tunnel.sales.clubId,
          clubCode: tunnel.sales.clubCode,
          checkout: tunnel.sales.checkoutId,
        },
        tunnel,
      },
    });
    return query;
  };

  const sendSms = async () => {
    const [query] = Api.POST({
      path: "/api/abo/resamania2/sign_mandates",
      body: {
        tunnelUid: tunnel.uid,
        msdsId: tunnel.club.msdsId,
        sign_mandate: {
          contactId: tunnel.mandate.contactId,
          mandateId: tunnel.mandate["@id"],
          saleId: tunnel.sales["@id"],
        },
      },
    });
    return query;
  };

  const askToSign = (e) => {
    e && e.preventDefault();

    setError(false);
    smsSendingSet(true);

    return sendSms()
      .then((ret) => {
        smsSendingSet(false);
        setTunnel({ ...tunnel, sign_request: ret });
      })
      .catch((e) => {
        smsSendingSet(false);
        setError("Problème d'envoi SMS");
        console.error(e);
      });
  };

  const openPdf = () => {
    const byteCharacters = atob(contract);
    const byteNumbers = new Array(byteCharacters.length).fill().map((_, i) => byteCharacters.charCodeAt(i));
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: "application/pdf" });
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, "_blank");
  };

  if (!contract) {
    return <LoadingScreen LoadingText={t("loading_screen.loading_contract")} />;
  }

  if (step === "SMS") return <p>Code sms en cours d'envoi</p>;

  if (step === "CONFIRM") return <Redirect push to="/confirmation"></Redirect>;

  if (step === "CARD") return <Redirect push to="/card"></Redirect>;

  return (
    <>
      <main className="bg-oaBackgroundColor min-h-screen w-full">
        <Link
          to="/user"
          className="absolute left-4 top-5 md:left-20 flex flex-row hover:text-oaRed transition-all duration-300"
        >
          <ChevronLeft className="mr-2" />
          {t("previous_step")}
        </Link>
        <section className="w-full h-full flex flex-col items-center px-5 py-4 md:px-20">
          <div className="w-full animate-fade-in-left mt-auto">
            <Breadcrumbs />
          </div>
          <div className="text-lg sm:text-[40px] font-bold text-center py-6 sm:py-12 animate-fade-in-left">
            {t("contract.title").toUpperCase()}
          </div>

          <section className="mb-8 w-full flex justify-center flex-col items-center ">
            <iframe
              className="w-3/5 aspect-[3/4]	md:w-96 "
              title="contrat"
              src={"data:application/pdf;base64," + contract}
            />
            <button
              onClick={() => openPdf()}
              className="w-4/6	md:w-60 p-2 bg-[#8A8E9B1A] bg-oaRed rounded-[4px] font-extrabold text-white mt-6"
            >
              {t("contract.download").toUpperCase()}
            </button>
          </section>

          <form className="flex flex-col items-center" onSubmit={onSubmit}>
            <div>
              <div className="text-lg font-medium text-center mb-6 animate-fade-in-left">{t("contract.info")}</div>
              <fieldset className="flex flex-col lg:flex-row items-center md:gap-3 gap-4 mb-4">
                <div className="flex flex-row items-end min-w-fit">
                  <OAMaskInput
                    className={"h-10 text-center border border-oaPlaceholderText "}
                    value={code}
                    onChange={(e) => codeSet(e.target.value)}
                    type="string"
                    name={"sms"}
                    mask={"* * * * * *"}
                    placeholder={t("contract.sms_ph")}
                    slotChar={"_ _ _ _ _ _"}
                  />
                </div>
                <div className="w-full flex flex-col justify-center md:justify-start lg:flex-row md:gap-2 items-center">
                  <p className="text-base font-medium">{t("contract.no_sms")}</p>
                  <button type="button" className="text-base font-semibold underline" onClick={askToSign}>
                    {t("contract.resend_sms")}
                  </button>
                  {!!smsSending && <p>{t("contract.sms_sending")}</p>}
                </div>
              </fieldset>
            </div>

            {!!error && <p className="text-oaRed">{error}</p>}

            {step !== "LOADING" && (
              <button
                onClick={onSubmit}
                className={
                  "w-4/6 md:w-60 text-center p-2 text-base bg-oaRed rounded-[4px] font-extrabold text-white mt-6"
                }
              >
                {t("contract.validate").toUpperCase()}
              </button>
            )}
          </form>
        </section>
      </main>
      <footer>
        <Footer></Footer>
      </footer>
    </>
  );
}
